import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { api } from '@/api/client';

interface FormData {
  name: string;
  code?: string;
  stateId?: number | '';
  city?: string;
  address?: string;
  pincode?: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  isActive?: boolean;
}

interface State { id: number; name: string }

interface Props {
  onSuccess: () => void;
  branchId?: number;
  defaultValues?: Partial<FormData>;
}

export function CreateBranchForm({ onSuccess, branchId, defaultValues }: Props) {
  const qc = useQueryClient();
  const isEdit = !!branchId;

  const { data: states } = useQuery({
    queryKey: ['states'],
    queryFn: async () => (await api.get<State[]>('/states')).data,
    staleTime: 1000 * 60 * 60,
  });

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    defaultValues: { isActive: true, ...defaultValues },
  });

  const save = useMutation({
    mutationFn: async (data: FormData) => {
      const payload: any = { ...data };
      if (payload.stateId === '' || payload.stateId === undefined) delete payload.stateId;
      else payload.stateId = Number(payload.stateId);
      Object.keys(payload).forEach(k => { if (payload[k] === '') delete payload[k]; });
      if (isEdit) {
        const r = await api.patch(`/branches/${branchId}`, payload);
        return r.data;
      }
      const r = await api.post('/branches', payload);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['branches'] });
      toast.success(isEdit ? 'Branch updated' : 'Branch created');
      onSuccess();
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message ?? 'Failed to save branch');
    },
  });

  return (
    <form onSubmit={handleSubmit((d) => save.mutate(d))} className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Branch Name *" error={errors.name?.message}>
          <input {...register('name', { required: 'Required', minLength: { value: 2, message: 'Min 2 chars' } })}
            placeholder="Mumbai Hub" className="input" />
        </Field>
        <Field label="Code">
          <input {...register('code', { maxLength: { value: 20, message: 'Max 20 chars' } })}
            placeholder="MUM-01" className="input" />
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label="State">
          <select {...register('stateId')} className="input">
            <option value="">— Select state —</option>
            {states?.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </Field>
        <Field label="City">
          <input {...register('city')} placeholder="Mumbai" className="input" />
        </Field>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Field label="Pincode">
          <input {...register('pincode', { pattern: { value: /^\d{0,6}$/, message: '6 digits' } })}
            placeholder="400001" className="input" />
        </Field>
        <div className="col-span-2">
          <Field label="Address">
            <input {...register('address')} placeholder="Plot 42, Andheri East" className="input" />
          </Field>
        </div>
      </div>

      <div className="border-t border-slate-100 pt-4">
        <div className="text-sm font-medium text-slate-700 mb-2">Branch Contact</div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Contact Name">
            <input {...register('contactName')} className="input" />
          </Field>
          <Field label="Contact Phone">
            <input {...register('contactPhone')} placeholder="9999999999" className="input" />
          </Field>
        </div>
        <Field label="Contact Email">
          <input type="email" {...register('contactEmail')} className="input" />
        </Field>
      </div>

      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" {...register('isActive')} className="rounded" />
        Active
      </label>

      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onSuccess} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
        <button type="submit" disabled={save.isPending}
          className="px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary-700 transition disabled:opacity-60 flex items-center gap-2">
          {save.isPending && <Loader2 size={16} className="animate-spin" />}
          {isEdit ? 'Save Changes' : 'Create Branch'}
        </button>
      </div>
    </form>
  );
}

function Field({ label, children, error }: { label: string; children: React.ReactNode; error?: string }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      {children}
      {error && <p className="text-danger text-xs mt-1">{error}</p>}
    </div>
  );
}
